import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Flame, Gem, ShieldCheck, MapPin } from "lucide-react";
import { SEO, localBusinessSchema } from "@/components/SEO";
import { Marquee } from "@/components/Marquee";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { BUSINESS } from "@/data/business";
import { SERVICES } from "@/data/services";

const HERO_IMG =
  "https://images.unsplash.com/photo-1651065213855-e6094f99ee65?crop=entropy&cs=srgb&fm=jpg&q=85";

const headlineWords = ["Still", "The", "Mind.", "Restore", "The", "Body."];

const BENTO_CLASS = {
  "full-body-massage": "bento-a",
  "deep-tissue-massage": "bento-b",
  "thai-massage": "bento-c",
  "aromatherapy-massage": "bento-d",
};

const PHILOSOPHY = [
  {
    icon: Gem,
    title: "Botanical Elixirs",
    text: "Therapeutic-grade oils and ritual blends sourced for purity and depth of scent.",
  },
  {
    icon: ShieldCheck,
    title: "Master Therapists",
    text: "Skilled hands trained in traditional, energy-line & deep therapeutic technique.",
  },
  {
    icon: Flame,
    title: "Private Sanctuary",
    text: "Dim, incense-lit treatment rooms designed for complete, undisturbed stillness.",
  },
  {
    icon: MapPin,
    title: "Central Ameerpet",
    text: "Easy to reach on Greenlands Road, Divyashakti Apartments, Ameerpet.",
  },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1611441085337-56f4d0ea0ae2?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1698897548289-b4e96ed41b56?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1636714528228-f469eefb3eef?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1704211553100-dad83bf34994?crop=entropy&cs=srgb&fm=jpg&q=85",
];

const TESTIMONIALS = [
  {
    quote:
      "The deep tissue ritual finally got rid of my chronic shoulder knots. The room was so still I nearly fell asleep.",
    name: "Rohit V.",
  },
  {
    quote:
      "The Thai massage left my back more flexible than it's been in years. Booked my next session before I even left.",
    name: "Sana K.",
  },
  {
    quote:
      "Loved the aromatherapy blend they used — the whole space feels like a hidden sanctuary in Ameerpet.",
    name: "Divya N.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.6, 0.85]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <SEO
        title="The Soul Skin Spa & Salon | Luxury Massage Spa in Ameerpet, Hyderabad"
        description="The Soul Skin Spa & Salon offers full body, deep tissue, Thai & aromatherapy massage in Ameerpet, Hyderabad. Call +91 7995280296 to book your ritual."
        path="/"
        schema={localBusinessSchema()}
      />

      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative h-[100vh] min-h-[640px] overflow-hidden flex items-end bg-ss-bg"
      >
        <motion.div
          style={{ y: imgY }}
          className="absolute inset-0 -top-[10%] h-[120%] w-full"
        >
          <img
            src={HERO_IMG}
            alt="Dark luxury spa sanctuary interior with bamboo wall at The Soul Skin Spa & Salon"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-t from-ss-bg via-ss-bg/60 to-ss-bg/20"
        />

        {/* floating gold particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden hidden sm:block">
          {[...Array(8)].map((_, i) => (
            <span
              key={i}
              className={`absolute rounded-full bg-ss-gold/60 ${
                i % 2 ? "animate-float-slower" : "animate-float-slow"
              }`}
              style={{
                width: `${4 + (i % 3) * 3}px`,
                height: `${4 + (i % 3) * 3}px`,
                left: `${10 + i * 11}%`,
                top: `${20 + (i % 4) * 15}%`,
                opacity: 0.4,
              }}
            />
          ))}
        </div>

        <motion.div
          style={{ opacity: contentOpacity }}
          className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28 w-full"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-ss-gold mb-5">
            Ameerpet, Hyderabad
          </p>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold text-ss-text leading-[1.05] max-w-3xl">
            {headlineWords.map((word, i) => (
              <span key={i} className="inline-block overflow-hidden mr-3 sm:mr-4 align-bottom">
                <motion.span
                  className="inline-block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 0.9,
                    delay: 0.15 + i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-6 text-base sm:text-lg text-ss-textMuted max-w-md"
          >
            Full body, deep tissue, Thai & aromatherapy massage rituals in a
            dark luxury sanctuary at The Soul Skin Spa & Salon.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <a
              href={`tel:${BUSINESS.phoneClean}`}
              data-testid="hero-call-button"
              className="flex items-center gap-2.5 bg-ss-gold text-ss-bg px-7 py-4 rounded-full text-sm font-semibold hover:bg-ss-goldHover transition-all duration-300"
            >
              <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
            </a>
            <Link
              to="/services/full-body-massage"
              data-testid="hero-view-services-button"
              className="flex items-center gap-2.5 border border-ss-gold/30 text-ss-text px-7 py-4 rounded-full text-sm font-medium hover:bg-ss-gold/10 transition-all duration-300"
            >
              View Services
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={[
          "DEEP TISSUE THERAPY",
          "THAI MASSAGE",
          "FULL BODY MASSAGE",
          "AROMATHERAPY MASSAGE",
          "THE SOUL SKIN SPA & SALON",
        ]}
      />

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-ss-gold mb-4">
            Our Rituals
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ss-text max-w-xl">
            Four signature therapies, one dark sanctuary
          </h2>
        </Reveal>

        <div className="services-bento mt-12" data-testid="services-bento-grid">
          {SERVICES.map((service, i) => (
            <Reveal key={service.slug} delay={i * 0.08} className={BENTO_CLASS[service.slug]}>
              <Link
                to={service.path}
                data-testid={`service-card-${service.slug}`}
                className="group relative block h-full min-h-[220px] rounded-2xl overflow-hidden border border-ss-gold/10"
              >
                <img
                  src={service.cardImage}
                  alt={`${service.name} ambient decor at The Soul Skin Spa & Salon`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ss-bg via-ss-bg/40 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-7">
                  <h3 className="font-heading text-xl sm:text-2xl font-bold text-ss-text">
                    {service.name}
                  </h3>
                  <p className="text-ss-textMuted text-sm mt-2 max-w-xs hidden sm:block">
                    {service.excerpt}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-ss-gold text-sm font-medium group-hover:gap-3 transition-all duration-300">
                    Explore Ritual →
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-ss-bgAlt py-24 sm:py-32 border-y border-ss-gold/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-ss-gold mb-4">
              Why Soul Skin
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ss-text max-w-xl">
              A sanctuary built on care & craft
            </h2>
          </Reveal>

          <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {PHILOSOPHY.map((item) => (
              <RevealItem key={item.title}>
                <div
                  data-testid={`philosophy-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-ss-surface rounded-2xl p-7 h-full border border-ss-gold/10 hover:border-ss-gold/30 transition-colors duration-300"
                >
                  <item.icon className="w-7 h-7 text-ss-gold" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-semibold mt-5 text-ss-text">
                    {item.title}
                  </h3>
                  <p className="text-sm text-ss-textMuted mt-2 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-ss-gold mb-4">
            The Atmosphere
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ss-text max-w-xl">
            Candlelight, stone & elemental calm
          </h2>
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {GALLERY.map((src, i) => (
            <Reveal key={src} delay={i * 0.08} className="rounded-2xl overflow-hidden aspect-[3/4] border border-ss-gold/10">
              <img
                src={src}
                alt="Ambient spa decor at The Soul Skin Spa & Salon"
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
              />
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-ss-bgAlt py-24 sm:py-32 border-y border-ss-gold/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-ss-gold mb-4">
              Guest Reflections
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ss-text max-w-xl">
              Kind words from our guests
            </h2>
          </Reveal>

          <RevealGroup className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14">
            {TESTIMONIALS.map((t) => (
              <RevealItem key={t.name}>
                <div
                  data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                  className="bg-ss-surface border border-ss-gold/10 rounded-2xl p-7 h-full"
                >
                  <p className="font-heading text-lg text-ss-text/90 leading-relaxed italic">
                    “{t.quote}”
                  </p>
                  <p className="text-sm text-ss-gold mt-5">— {t.name}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center">
        <Reveal>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ss-text">
            Ready to unwind?
          </h2>
          <p className="text-ss-textMuted mt-4 max-w-md mx-auto">
            Call or WhatsApp us directly to book your ritual — no forms, no
            waiting.
          </p>
          <a
            href={`tel:${BUSINESS.phoneClean}`}
            data-testid="cta-section-call-button"
            className="inline-flex items-center gap-2.5 bg-ss-gold text-ss-bg px-8 py-4 rounded-full text-sm font-semibold mt-8 hover:bg-ss-goldHover transition-all duration-300"
          >
            <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
          </a>
        </Reveal>
      </section>
    </>
  );
};

export default Home;
